import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import {
    ComposeNotificationDialogComponent,
    ComposeNotificationDialogData,
} from 'app/layout/common/notifications/compose-notification-dialog.component';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import {
    Notification,
    senderInitials,
} from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        RouterLink,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    canSend: boolean = false;
    initials = senderInitials;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _matDialog: MatDialog,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _router: Router,
        private _viewContainerRef: ViewContainerRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Lab employees and Dankospark support staff; the server enforces this too
        this.canSend = this._notificationsService.canSend;

        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                this.notifications = notifications;
                this._changeDetectorRef.markForCheck();
            });

        // The unread count comes from the server, as the panel only holds the latest notifications
        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                this.unreadCount = count;
                this._updateNavigationBadge(count);
                this._changeDetectorRef.markForCheck();
            });

        // Re-check for new notifications while the bell is on screen
        this._notificationsService
            .poll()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );

        // Pick up anything that arrived since the last poll
        this._notificationsService.refresh();
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef?.detach();
    }

    /**
     * Open the compose dialog
     */
    compose(): void {
        this.closePanel();

        this._matDialog.open<ComposeNotificationDialogComponent, ComposeNotificationDialogData>(
            ComposeNotificationDialogComponent,
            { data: { currentUrl: this._router.url } }
        );
    }

    /**
     * Reply to the sender of the given notification
     */
    reply(notification: Notification): void {
        this.closePanel();

        this._matDialog.open<ComposeNotificationDialogComponent, ComposeNotificationDialogData>(
            ComposeNotificationDialogComponent,
            { data: { currentUrl: this._router.url, replyTo: notification } }
        );
    }

    /**
     * Follow the notification's link, marking it read
     */
    open(notification: Notification): void {
        if (!notification.read) {
            this.toggleRead(notification);
        }

        this.closePanel();
        this._router.navigateByUrl(notification.link);
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void {
        // Toggle the read status straight away; the service reloads the list afterwards
        notification.read = !notification.read;
        this.unreadCount += notification.read ? -1 : 1;

        this._notificationsService
            .setRead(notification, notification.read)
            .subscribe();
    }

    /**
     * Delete the given notification from my inbox
     */
    delete(notification: Notification): void {
        this._notificationsService.delete(notification).subscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Show the unread count on the "Notifications" item of the main navigation
     *
     * @private
     */
    private _updateNavigationBadge(count: number): void {
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        if (!navigation?.navigation) {
            return;
        }

        const item = this._fuseNavigationService.getItem(
            'notifications',
            navigation.navigation
        );

        if (!item) {
            return;
        }

        item.badge =
            count > 0
                ? {
                      title: String(count),
                      classes: 'px-2 bg-teal-600 text-white rounded-full',
                  }
                : undefined;
        navigation.refresh();
    }
}
